/**
 * BetaActivityTracker — silent activity ping for pre-launch pilot testers.
 *
 * Mounted at the root of `<App>`. Reads the `district_beta_tester_id` +
 * `district_beta_session_id` populated by SiteGate on successful login and
 * fires activity beacons to the backend on every route change, on every
 * `district:beta-action` custom event, and on time-on-page transitions.
 *
 * Renders nothing. Testers cannot see this exists — no visible affordance,
 * no console warnings, no chrome — this is a pre-launch instrumentation
 * feature per product-owner decision.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BETA_TESTER_KEY, BETA_SESSION_KEY } from './SiteGate';

const _getIds = () => {
  try {
    return {
      testerId:  localStorage.getItem(BETA_TESTER_KEY),
      sessionId: localStorage.getItem(BETA_SESSION_KEY),
    };
  } catch {
    return { testerId: null, sessionId: null };
  }
};

const _post = (payload) => {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const body = JSON.stringify(payload);
  // sendBeacon so unload-time pings survive tab close.
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`${backend}/api/site-gate/track`, blob);
      return;
    } catch { /* fall through */ }
  }
  try {
    fetch(`${backend}/api/site-gate/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
};

export default function BetaActivityTracker() {
  const location = useLocation();
  const currentPathRef = useRef({ path: null, since: null });

  // Flush time-on-page when the path changes (or on unload).
  const flushTime = () => {
    const { testerId, sessionId } = _getIds();
    if (!testerId) return;
    const cur = currentPathRef.current;
    if (!cur.path || !cur.since) return;
    const durationMs = Date.now() - cur.since;
    if (durationMs < 200) return; // ignore instantaneous bounces
    _post({
      testerId, sessionId,
      kind: 'time',
      path: cur.path,
      durationMs,
    });
  };

  // Fire page_view + start tracking time on this new path.
  useEffect(() => {
    const { testerId, sessionId } = _getIds();
    if (!testerId) return;
    flushTime();
    const newPath = location.pathname + (location.search || '') + (location.hash || '');
    _post({
      testerId, sessionId,
      kind: 'page_view',
      path: newPath,
      meta: { referrer: currentPathRef.current.path || null },
    });
    currentPathRef.current = { path: newPath, since: Date.now() };
  }, [location.pathname, location.search, location.hash]);

  // Flush on tab close / navigation away.
  useEffect(() => {
    const onUnload = () => flushTime();
    window.addEventListener('beforeunload', onUnload);
    // visibilitychange also fires when a tab is backgrounded on mobile.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushTime();
    });
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  // Custom action events. Any code path can dispatch
  //   window.dispatchEvent(new CustomEvent('district:beta-action',
  //     { detail: { action: 'case_note.signed', meta: {...} } }));
  // and it lands in the same activity timeline as page views.
  useEffect(() => {
    const h = (e) => {
      const { testerId, sessionId } = _getIds();
      if (!testerId) return;
      const detail = e?.detail || {};
      _post({
        testerId, sessionId,
        kind: 'action',
        path: window.location.pathname,
        meta: { action: detail.action || 'unknown', ...(detail.meta || {}) },
      });
    };
    window.addEventListener('district:beta-action', h);
    return () => window.removeEventListener('district:beta-action', h);
  }, []);

  return null;
}
